import jwtDecode from 'jwt-decode';
import { Component } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import MenuEl from '../models/MenuEl';
import { navigate } from './navigationService';
import { AppState } from '../store/rootReducer';
import * as actions from '../store/actions/index';
import U from './Utils';
import { tabCommunication, BroadcastMessage } from './TabCommunicationService';

interface Props {
  auth: boolean;
  menuItems: MenuEl[];
  dispatch: Dispatch;
}

interface State {
  isRequestingToken: boolean;
  hasCheckedOtherTabs: boolean;
}

// Map state to props
function mapStateToProps(state: AppState) {
  return {
    auth: state.auth.authenticated,
    menuItems: state.menuItem.menuItems,
  };
}

// Map dispatch to props
const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatch
});

// Higher Order Component
export default (ChildComponent: React.ComponentType<any>, group: any = undefined) => {
  class ComposedComponent extends Component<Props, State> {
    private tokenRequestTimeout: NodeJS.Timeout | null = null;

    constructor(props: Props) {
      super(props);
      this.state = {
        isRequestingToken: false,
        hasCheckedOtherTabs: false
      };
    }

    /**
     * Component lifecycle - setup message handlers and check authentication
     */
    componentDidMount() {
      this.setupTokenMessageHandler();
      this.shouldNavigateAway();
    }

    /**
     * Component lifecycle - recheck authentication on updates
     */
    componentDidUpdate() {
      this.shouldNavigateAway();
    }

    /**
     * Component lifecycle - cleanup message handlers and timeouts
     */
    componentWillUnmount() {
      this.cleanupTokenHandlers();
    }

    /**
     * Clean up BroadcastChannel message handlers and timeouts
     */
    private cleanupTokenHandlers() {
      tabCommunication.offMessage('TOKEN_RESPONSE');
      tabCommunication.offMessage('TOKEN_REQUEST');
      if (this.tokenRequestTimeout) {
        clearTimeout(this.tokenRequestTimeout);
        this.tokenRequestTimeout = null;
      }
    }

    /**
     * Setup BroadcastChannel message handlers for cross-tab token communication
     */
    private setupTokenMessageHandler() {
      // Handle token responses from other tabs
      tabCommunication.onMessage('TOKEN_RESPONSE', (message: BroadcastMessage) => {
        if (message.data?.token && this.state.isRequestingToken) {
          // Clear the timeout since we received a token
          if (this.tokenRequestTimeout) {
            clearTimeout(this.tokenRequestTimeout);
            this.tokenRequestTimeout = null;
          }
          
          this.setState({ isRequestingToken: false, hasCheckedOtherTabs: true });
          
          // Use the token from another tab to perform autologin
          const urlParams = new URLSearchParams(window.location.search);
          const transId = urlParams.get('transid') || '';
          
          // Dispatch autologin action with the received token
          this.props.dispatch(actions.userAutologinToAutodataAuthGet(
            message.data.token, 
            transId, 
            false, // performSilent
            0 // loginStateUI
          ));
        }
      });

      // Handle token requests from other tabs
      tabCommunication.onMessage('TOKEN_REQUEST', (message: BroadcastMessage) => {
        const token = sessionStorage.getItem('token');
        if (token && token && token.trim() !== '' && token.trim() !== '') {
          // Send token to requesting tab
          tabCommunication.sendToken({
            token: token
          });
        }
      });
    }

    /**
     * Request authentication token from other browser tabs/windows
     */
    private requestTokenFromOtherTabs() {
      if (this.state.isRequestingToken || this.state.hasCheckedOtherTabs) {
        return; // Already requesting or already checked
      }

      this.setState({ isRequestingToken: true });
      
      // Request token from other tabs
      tabCommunication.requestToken();

      // Set timeout to stop waiting after 400ms
      this.tokenRequestTimeout = setTimeout(() => {
        if (this.state.isRequestingToken) {
          this.setState({ isRequestingToken: false, hasCheckedOtherTabs: true });
          // No token received, navigate to home
          navigate(U.HOMEPAGE_CONTEXT + '/home');
        }
      }, 400);
    }

    /**
     * Check authentication status and navigate away if not authorized
     */
    private shouldNavigateAway() {
      const token = sessionStorage.getItem('token');
      
      if (token) {
        try {
          const { exp, Groups }: { exp: number, Groups: string[] } = jwtDecode(token);
          const expirationTime = (exp * 1000) - 60000; // 1 minute buffer
          
          // Check if token is expired or user is not authenticated
          if (!this.props.auth || Date.now() >= expirationTime) {
            sessionStorage.removeItem('token');
            this.requestTokenFromOtherTabs();
            return;
          }
          
          // Check role-based access
          if (group && this.props.menuItems.length > 0) {
          const lookup = U.getTransactionsRole(this.props.menuItems, group);
          if (lookup) {
            if (!Groups.includes(lookup)) {
              navigate(U.HOMEPAGE_CONTEXT + '/menu');
            }
          } else {
            navigate(U.HOMEPAGE_CONTEXT + '/menu');
          }
        }
        } catch (error) {
          // Invalid token format
          console.warn('Invalid token format:', error);
          sessionStorage.removeItem('token');
          this.requestTokenFromOtherTabs();
        }
      } else {
        // No token found, check other tabs before navigating away
        if (!this.state.hasCheckedOtherTabs && !this.state.isRequestingToken) {
          this.requestTokenFromOtherTabs();
        } else if (this.state.hasCheckedOtherTabs && !this.state.isRequestingToken) {
          // Already checked other tabs and no token found
          navigate(U.HOMEPAGE_CONTEXT + '/home');
        }
        // If currently requesting, do nothing and wait
      }
    }
    //
    render() {
      return <ChildComponent {...this.props} />;
    }
  }

  return connect(mapStateToProps, mapDispatchToProps)(ComposedComponent);
};
